import React from "react";
import { Handle, Position, type NodeProps } from "reactflow";
import { FiDatabase, FiLayers } from "react-icons/fi";
import { type DataMartNodeData } from "./types";
import { SchemaTableName } from "../../mapping/components/SchemaTableName";

export function FactNode({ data, selected }: NodeProps<DataMartNodeData>): JSX.Element {
  const { entity, onSelect } = data;
  if (entity.kind !== "fact") {
    return <></>;
  }
  const isInternalRegistered =
    entity.record.target_entity_dm_origin === "internal_registered" ||
    entity.record.source_entity_id != null;
  const dataMartLabel = entity.record.datamart?.trim() ?? "";
  const connectorPositions = [Position.Top, Position.Right, Position.Bottom, Position.Left];
  const renderHandle = (position: Position) => (
    <React.Fragment key={`fact-${position}`}>
      <Handle
        type="source"
        id={`fact-${position}-source`}
        position={position}
        className="!tw-h-1 !tw-w-1 !tw-border-0 !tw-bg-transparent"
        style={{ pointerEvents: "none" }}
      />
      <Handle
        type="target"
        id={`fact-${position}-target`}
        position={position}
        className="!tw-h-3 !tw-w-3 !tw-border tw-border-sky-200 !tw-bg-white"
      />
    </React.Fragment>
  );

  return (
    <div
      onClick={(event) => {
        if (event.shiftKey || event.metaKey || event.ctrlKey) {
          return;
        }
        onSelect(entity.id);
      }}
      className={`tw-min-w-[220px] tw-cursor-grab tw-rounded-3xl tw-border tw-border-sky-200 tw-bg-gradient-to-br tw-from-white tw-via-sky-50 tw-to-sky-100 tw-p-5 tw-shadow-lg tw-transition-transform tw-duration-150 hover:tw--translate-y-1 ${
        selected ? "tw-ring-2 tw-ring-sky-400 tw-ring-offset-2" : "tw-ring-1 tw-ring-sky-200/60"
      }`}
    >
      {connectorPositions.map((position) => renderHandle(position))}
      <div className="tw-flex tw-items-center tw-gap-3">
        <div className="tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-2xl tw-bg-sky-500 tw-text-white tw-shadow-md">
          <FiDatabase className="tw-h-5 tw-w-5" />
        </div>
        <div>
          <p className="tw-text-sm tw-font-semibold tw-text-slate-900">Fact</p>
          <p className="tw-text-[11px] tw-font-medium tw-uppercase tw-tracking-wide tw-text-sky-700">
            Fact
          </p>
        </div>
        {isInternalRegistered ? (
          <div className="tw-ml-auto tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-border tw-border-sky-100 tw-bg-white/90 tw-px-2 tw-py-1 tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-[0.14em] tw-text-sky-700">
            Internal
          </div>
        ) : null}
      </div>
      <div className="tw-mt-3 tw-space-y-1">
        <SchemaTableName
          schema={entity.record.target_schema}
          table={entity.record.target_entity_dm_name}
          layout="stacked"
          className="tw-block tw-font-mono tw-text-[13px] tw-font-semibold tw-leading-5 tw-text-slate-900 tw-break-words"
        />
        {dataMartLabel ? (
          <div className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-white/80 tw-px-2 tw-py-1 tw-text-[10px] tw-font-semibold tw-text-slate-600">
            <FiLayers className="tw-h-3 tw-w-3 tw-text-sky-500" />
            <span>{dataMartLabel}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}
